/**
 * Automation Canvas Background Component
 *
 * Follows Note-Edgeless-Background pattern for correct positioning.
 * EXACT COPY of EdgelessDatabaseBackground pattern for Frame Preview support.
 */

import { DefaultTheme } from '@blocksuite/affine-model';
import { ThemeProvider } from '@blocksuite/affine-shared/services';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import {
  type BlockStdScope,
  PropTypes,
  requiredProperties,
  ShadowlessElement,
  stdContext,
} from '@blocksuite/std';
import { consume } from '@lit/context';
import { computed, effect } from '@preact/signals-core';
import { nothing } from 'lit';
import { property } from 'lit/decorators.js';

import type { AutomationBlockModel } from '../../schemas/index.js';
import { automationBackground } from '../automation-canvas-block.css.js';

@requiredProperties({
  automation: PropTypes.instanceOf(Object),
})
export class AutomationCanvasBackground extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  readonly backgroundStyle$ = computed(() => {
    const themeProvider = this.std.get(ThemeProvider);
    const theme = themeProvider.theme$.value;

    // Resolve the exact default Page/Note card surface in every theme.
    const backgroundColor = themeProvider.generateColorProperty(
      DefaultTheme.noteBackgrounColor,
      DefaultTheme.noteBackgrounColor,
      theme
    );

    // Use fixed default values like Database (no dynamic props access)
    const borderRadius = 8;
    return {
      borderRadius: borderRadius + 'px',
      backgroundColor: backgroundColor,
    };
  });

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add(automationBackground);
    this.disposables.add(
      effect(() => {
        Object.assign(this.style, this.backgroundStyle$.value);
      })
    );
  }

  override render() {
    return nothing;
  }

  @consume({ context: stdContext })
  accessor std!: BlockStdScope;

  @property({ attribute: false })
  accessor editing: boolean = false;

  @property({ attribute: false })
  accessor automation!: AutomationBlockModel;
}

declare global {
  interface HTMLElementTagNameMap {
    'automation-canvas-background': AutomationCanvasBackground;
  }
}
